import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type Detector = { detect: (source: CanvasImageSource) => Promise<{ rawValue: string }[]> };
type DetectorCtor = new (opts: { formats: string[] }) => Detector;

/**
 * Scans a QR code with the camera (WEB-04): the browser's BarcodeDetector where it exists, otherwise jsQR
 * (Apache-2.0, loaded only when needed) on frames drawn to a canvas. Calls onResult with the first protovision
 * label it reads and stops the camera when unmounted.
 */
export function QrScanner({ onResult }: { onResult: (text: string) => void }) {
  const { t } = useTranslation();
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let stream: MediaStream | undefined;
    let timer: number | undefined;
    let stopped = false;

    async function start() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      } catch {
        setError(t("addDevice.cameraDenied"));
        return;
      }
      if (stopped || !video.current) return;
      video.current.srcObject = stream;
      await video.current.play().catch(() => undefined);

      const Native = (window as unknown as { BarcodeDetector?: DetectorCtor }).BarcodeDetector;
      const detector = Native ? new Native({ formats: ["qr_code"] }) : undefined;
      const jsQR = detector ? undefined : (await import("jsqr")).default;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d", { willReadFrequently: true });

      const tick = async () => {
        const v = video.current;
        if (stopped || !v || v.readyState < 2) return;
        let text: string | undefined;
        if (detector) {
          text = (await detector.detect(v).catch(() => []))[0]?.rawValue;
        } else if (jsQR && ctx) {
          canvas.width = v.videoWidth;
          canvas.height = v.videoHeight;
          ctx.drawImage(v, 0, 0);
          const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
          text = jsQR(img.data, img.width, img.height)?.data;
        }
        if (text?.toUpperCase().startsWith("PV1:")) onResult(text);
      };
      timer = window.setInterval(() => void tick(), 250);
    }

    void start();
    return () => {
      stopped = true;
      if (timer) window.clearInterval(timer);
      stream?.getTracks().forEach((tr) => tr.stop());
    };
  }, [onResult, t]);

  return (
    <div className="mt-3">
      {error ? (
        <p role="alert" className="text-sm text-red-800">
          {error}
        </p>
      ) : (
        <video
          ref={video}
          muted
          playsInline
          aria-label={t("addDevice.cameraView")}
          className="w-full rounded-md bg-black"
        />
      )}
    </div>
  );
}
